// @flow
import { useDispatch } from "react-redux";

import { getSetting, HTTP_METHODS } from "../constants";

import type {
  RemoveModelByKeyAction,
  UpdateFormAction,
} from "../redux/_modularui";
import {
  loadModularUI,
  removeModelByKey,
} from "../redux/_modularui/ModularUIActions";

import {
  addRepeatableAttributeSet,
  cancelForm,
  cancelRepeatableAttributeSet,
  previousObject,
  removeRepeatableAttributeSet,
  showFormNotification,
  updateFormAttribute,
} from "../redux/actions";

import Href from "../models/href/Href";
import FormModel from "../models/form/FormModel";

import { useModularUI } from "./useModularUI";

import type { AttributeType, FormObjectModel, ModularUIModel } from "../models";
import type { UpdateFormOptions } from "../redux/types";

type FormNavigationHook = {
  previous: (form: FormModel) => UpdateFormAction,
  cancel: (form: FormModel) => void,
  submit: (form: FormModel) => void,
  showFormNotification: (form: FormModel) => void,
  remove: (form: FormModel) => RemoveModelByKeyAction,
};

type AttributeSetHook = {
  save: () => UpdateFormAction,
  cancel: (object: FormObjectModel) => UpdateFormAction,
  remove: (object: FormObjectModel) => UpdateFormAction,
};

type AttributeUpdateHook = (
  attribute: AttributeType,
  value: string,
  options: UpdateFormOptions,
) => void;

/**
 * Load a form by href
 */
export const useForm = (href: string | Href, data?: Object): ?FormModel => {
  const formHref = new Href(href);

  if (!getSetting("ALWAYS_COMMIT_FORM")) {
    formHref.addParameter("commit", "false");
  }

  const form = useModularUI("form", formHref, {
    method: HTTP_METHODS.POST,
    targetModel: FormModel,
    removeOnUnmount: true,
    data,
    origin: formHref.origin,
    contextPath: formHref.contextPath,
  });

  if (form?.model) {
    return form.model;
  }
};

/**
 * Form navigation methods
 */
export const useFormNavigation = (): FormNavigationHook => {
  const dispatch = useDispatch();

  const previous = (form: FormModel) => dispatch(previousObject(form));

  const cancel = (form: FormModel) => dispatch(cancelForm(form));

  const submit = (form: FormModel) =>
    dispatch(
      loadModularUI(form.connectKey, form.selfhref, {
        method: HTTP_METHODS.POST,
        data: form.formdata,
        updateHandler: (newModel: ModularUIModel): ModularUIModel => {
          if (newModel instanceof FormModel) {
            const clonedModel: FormModel = form.clone();
            clonedModel.update(newModel);
            return clonedModel;
          }
          return newModel;
        },
        targetModel: FormModel,
      }),
    );

  const remove = (form: FormModel) =>
    dispatch(removeModelByKey(form.connectKey));

  const showFormNotificationAction = (form: FormModel) =>
    dispatch(showFormNotification(form));

  return {
    previous,
    cancel,
    submit,
    remove,
    showFormNotification: showFormNotificationAction,
  };
};

/**
 * Hook that returns a function to update attributes of a form object.
 * <br >
 * The returned function accepts the following options:
 *
 * - autosubmit: Automatically submit the form after a change
 * - autosav: Automatically save the form (in the background) after a change
 * - autoupdate: Automatically update the current form object after a change
 * - forceUpdate: Force the update even if conditions are not met.
 * - validate: Activate/deactivate the form object validation by an update.
 */
export const useAttributeUpdate = (
  form: FormModel,
  object: FormObjectModel,
): AttributeUpdateHook => {
  const dispatch = useDispatch();

  return (
    attribute: AttributeType,
    value: string,
    options: UpdateFormOptions,
  ) => dispatch(updateFormAttribute(form, object, attribute, value, options));
};

/**
 * Attributeset actions
 */
export const useAttributeSet = (form: FormModel): AttributeSetHook => {
  const dispatch = useDispatch();

  return {
    save: () => dispatch(addRepeatableAttributeSet(form)),
    cancel: (object: FormObjectModel) =>
      dispatch(cancelRepeatableAttributeSet(form, object)),
    remove: (object: FormObjectModel) =>
      dispatch(removeRepeatableAttributeSet(form, object)),
  };
};
